import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  CardContent,
  CardActions,
  Button,
  Avatar,
} from '@mui/material';
import { GlassBackdrop, GlassSurface } from '../../../components/GlassDashboard';
import {
  People as UsersIcon,
  Security as SecurityIcon,
  Settings as SystemIcon,
  AdminPanelSettings as AdminIcon,
  VpnKey as RolesIcon,
  Shield as PermissionsIcon,
  Extension as ModulesIcon,
  Widgets as MiniModulesIcon,
  History as AuditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import logo from '../../../assets/images/logo.png';

const AdministrationOverview: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const { hasPermission } = usePermissions();

  const adminModules = [
    {
      title: 'Gestion des Utilisateurs',
      description: 'Créer, modifier et gérer les comptes utilisateurs',
      icon: <UsersIcon sx={{ fontSize: 40 }} />,
      color: '#F44336',
      path: '/administration/users',
      enabled: true,
      miniModuleName: 'Users',
    },
    {
      title: 'Gestion des Rôles',
      description: 'Créer et configurer les rôles utilisateurs',
      icon: <RolesIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      path: '/administration/roles',
      enabled: true,
      miniModuleName: 'Roles',
    },
    {
      title: 'Gestion des Permissions',
      description: 'Attribuer les permissions CRUD aux rôles',
      icon: <PermissionsIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
      path: '/administration/permissions',
      enabled: true,
      miniModuleName: 'Profiles',
    },
    {
      title: 'Gestion des Modules',
      description: 'Gérer les modules système (Stock, Config, Admin)',
      icon: <ModulesIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      path: '/administration/modules',
      enabled: true,
      miniModuleName: 'Modules',
      developerOnly: true,
    },
    {
      title: 'Gestion des Mini-Modules',
      description: 'Gérer les sous-modules et leurs permissions',
      icon: <MiniModulesIcon sx={{ fontSize: 40 }} />,
      color: '#00BCD4',
      path: '/administration/mini-modules',
      enabled: true,
      miniModuleName: 'MiniModules',
      developerOnly: true,
    },
    {
      title: 'Journal d\'Audit',
      description: 'Consulter l\'historique des actions et modifications',
      icon: <AuditIcon sx={{ fontSize: 40 }} />,
      color: '#607D8B',
      path: '/administration/audit-logs',
      enabled: true,
      miniModuleName: 'Audit Logs',
    },
    {
      title: 'Sécurité',
      description: 'Paramètres de sécurité et authentification',
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      color: 'warning',
      path: '/administration/security',
      enabled: false,
      miniModuleName: null,
    },
    {
      title: 'Logs Système',
      description: 'Consulter les journaux et activités système',
      icon: <SystemIcon sx={{ fontSize: 40 }} />,
      color: 'info',
      path: '/administration/logs',
      enabled: false,
      miniModuleName: null,
    },
    {
      title: 'Paramètres Avancés',
      description: 'Configuration avancée du système',
      icon: <AdminIcon sx={{ fontSize: 40 }} />,
      color: 'error',
      path: '/administration/settings',
      enabled: false,
      miniModuleName: null,
    },
  ];

  // Check if user has permission for a module
  const hasModulePermission = (module: any) => {
    // Coming soon items are always disabled
    if (!module.enabled) return false;

    // Check permissions if miniModuleName is specified
    if (module.miniModuleName) {
      return hasPermission('Admin', 'view', module.miniModuleName);
    }

    return true;
  };

  // Filter out developer-only modules for non-developers, then check permissions
  const visibleModules = adminModules
    .filter((module) => {
      // Developer-only items: hide completely from non-developers
      if (module.developerOnly) {
        const userRoleName = user?.user_role?.name?.toLowerCase() || '';
        return userRoleName === 'developer';
      }
      return true;
    })
    .map((module) => ({
      ...module,
      hasAccess: hasModulePermission(module),
    }));

  return (
    <Container maxWidth="lg" disableGutters sx={{ px: 0 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom>
          Module Administration
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Gestion des utilisateurs, rôles, permissions et système
        </Typography>
      </Box>

      <GlassBackdrop>
        <Grid container spacing={3}>
          {visibleModules.map((module) => (
            <Grid item xs={12} sm={6} md={4} key={module.title}>
              <GlassSurface
                accentColor={module.hasAccess ? module.color : undefined}
                interactive={module.hasAccess}
                onClick={module.hasAccess ? () => navigate(module.path) : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: module.hasAccess ? 1 : 0.6,
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: module.hasAccess ? module.color : 'grey.400',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {module.icon}
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  {module.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {module.description}
                </Typography>
                {!module.enabled && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}
                  >
                    Bientôt disponible
                  </Typography>
                )}
                {module.enabled && !module.hasAccess && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}
                  >
                    Accès non autorisé
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant={module.hasAccess ? "contained" : "outlined"}
                  disabled={!module.hasAccess}
                  sx={{
                    backgroundColor: module.hasAccess ? '#F44336' : 'transparent',
                    borderColor: '#F44336',
                    color: module.hasAccess ? 'white' : '#F44336',
                    '&:hover': {
                      backgroundColor: module.hasAccess ? '#D32F2F' : 'rgba(244, 67, 54, 0.1)',
                    }
                  }}
                  onClick={module.hasAccess ? (e) => {
                    e.stopPropagation();
                    navigate(module.path);
                  } : undefined}
                >
                  {module.hasAccess ? 'Accéder' : (!module.enabled ? 'Bientôt' : 'Accès refusé')}
                </Button>
              </CardActions>
              </GlassSurface>
            </Grid>
          ))}
        </Grid>

        <GlassSurface accentColor="#F44336" interactive={false} sx={{ mt: 6, p: 3 }}>
          <Typography variant="h6" color="error.main" gutterBottom>
            ⚠️ Zone d'Administration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ce module est réservé aux administrateurs système. Les modifications effectuées ici
            peuvent affecter l'ensemble du système. Procédez avec prudence.
          </Typography>
        </GlassSurface>
      </GlassBackdrop>
    </Container>
  );
};

export default AdministrationOverview;